import React, { useState, useMemo, useEffect } from "react";
import {
  MapPin,
  LocateFixed,
  Loader2,
  Calendar,
  ArrowRight,
  Navigation,
  Compass,
} from "lucide-react";
import { MapHeader } from "./UI";
import { getCategoryById } from "../services/CategoryModel";
import { useUser } from "../context/user";
import styles from "./MapScreen.module.css";

// Embedded Google Maps view + a horizontally scrollable "radar" feed of
// events, filterable by proximity or the user's interests and sortable by
// distance once GPS location is available.
export default function MapScreen({
  events,
  selectedEvent: externalSelectedEvent,
  onSelectEvent,
  onViewDetails, // Separate from onSelectEvent: this one opens the full detail screen
}) {
  const { userData } = useUser();
  const [selectedEvent, setSelectedEvent] = useState(
    externalSelectedEvent || null
  );
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [radarFilter, setRadarFilter] = useState("all"); // "all", "nearby", "interests"

  // Sync external selected event if it changes
  useEffect(() => {
    setSelectedEvent(externalSelectedEvent);
  }, [externalSelectedEvent]);

  // Safely extract user interests
  const userInterests = userData?.interests || [];

  // Dynamic Google Maps query builder
  const mapQuery = selectedEvent
    ? selectedEvent.lat && selectedEvent.lng
      ? `${selectedEvent.lat},${selectedEvent.lng}`
      : encodeURIComponent(`${selectedEvent.location}, Dublin, Ireland`)
    : userLocation
    ? `${userLocation.lat},${userLocation.lng}`
    : encodeURIComponent("Dublin City Center, Ireland");

  // Handler: Fetch user current GPS geolocation
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocating(false);
      },
      (error) => {
        console.error("Error getting user location: ", error);
        setLocating(false);
        alert("Unable to retrieve your location. Please check permissions.");
      },
      { enableHighAccuracy: true }
    );
  };

  // Haversine formula to calculate accurate distance in kilometers
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // MEMOIZED: Enrich events with calculated distance to avoid lags/flickering
  const processedEvents = useMemo(() => {
    return events.map((e) => {
      let distance = null;
      if (userLocation && e.lat && e.lng) {
        distance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          e.lat,
          e.lng
        );
      }
      return { ...e, distance };
    });
  }, [events, userLocation]);

  // MEMOIZED: Filter and sort events instantly and smoothly
  const filteredRadarEvents = useMemo(() => {
    return processedEvents
      .filter((e) => {
        if (radarFilter === "nearby") {
          if (!userLocation) return true;
          if (e.distance === null) return false;
          return e.distance <= 10.0; // 10 km radius
        }

        if (radarFilter === "interests") {
          if (userInterests.length === 0) return true;
          return userInterests.some(
            (interestId) =>
              e.category?.toLowerCase() === interestId.toLowerCase() ||
              e.title?.toLowerCase().includes(interestId.toLowerCase()) ||
              (Array.isArray(e.tags) &&
                e.tags.some(
                  (t) => t.toLowerCase() === interestId.toLowerCase()
                ))
          );
        }

        return true; // "all"
      })
      .sort((a, b) => {
        if (a.distance !== null && b.distance !== null) {
          return a.distance - b.distance;
        }
        return 0;
      });
  }, [processedEvents, radarFilter, userInterests]);

  // Clean handler for filter selection
  const handleFilterSelection = (newFilter) => {
    setRadarFilter(newFilter);
    if (newFilter === "nearby" && !userLocation) {
      handleGetLocation();
    }
  };

  // Local handler to update event selection
  const handleSelectEvent = (e) => {
    setSelectedEvent(e);
    if (onSelectEvent) {
      onSelectEvent(e);
    }
  };

  return (
    <div className={styles.container}>
      <MapHeader />

      {/* Official embedded map viewport */}
      <div className={styles.mapViewport}>
        <iframe
          key={mapQuery}
          title="Dublin Map"
          width="100%"
          height="100%"
          className={styles.mapIframe}
          loading="lazy"
          allowFullScreen
          src={`https://maps.google.com/maps?q=${mapQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
        ></iframe>

        {/* "My Location" action button */}
        <button
          onClick={handleGetLocation}
          disabled={locating}
          className={styles.locationButton}
          title="Center to my location"
        >
          {locating ? (
            <Loader2 size={16} className={styles.spinningIcon} />
          ) : (
            <LocateFixed size={16} className={styles.accentIcon} />
          )}
          <span>My Location</span>
        </button>

        {/* Active GPS indicator badge */}
        {userLocation && (
          <div className={styles.gpsBadge}>
            <span className={styles.gpsPingDot}></span>
            GPS Active ({userLocation.lat.toFixed(4)},{" "}
            {userLocation.lng.toFixed(4)})
          </div>
        )}

        {/* Floating card for selected map location */}
        {selectedEvent && (
          <div className={styles.floatingCard}>
            <div className={styles.truncateContent}>
              <span className={styles.selectedBadge}>Selected Location</span>
              <h4 className={styles.floatingCardTitle}>
                {selectedEvent.title}
              </h4>
              <p className={styles.floatingCardAddress}>
                📍 {selectedEvent.location}
              </p>
            </div>
            <div className={styles.floatingCardButtons}>
              {/* Opens the full detail screen via onViewDetails, not onSelectEvent */}
              <button
                onClick={() => {
                  if (onViewDetails) {
                    onViewDetails(selectedEvent);
                  }
                }}
                className={styles.detailsButton}
              >
                Details <ArrowRight size={12} />
              </button>
              <button
                onClick={() => handleSelectEvent(null)}
                className={styles.closeButton}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Radar Section / Horizontal Carousel Feed */}
      <div className={styles.radarSection}>
        <div className={styles.radarHeader}>
          <h3 className={styles.radarTitle}>
            <Compass size={14} className={styles.accentIcon} /> Dublin Radar
            Feed ({filteredRadarEvents.length})
          </h3>
          <span className={styles.radarHint}>Sorted by proximity</span>
        </div>

        {/* Filter Tabs for the Radar Feed */}
        <div className={styles.filterTabsContainer}>
          <button
            onClick={() => handleFilterSelection("all")}
            className={`${styles.filterTabBtn} ${
              radarFilter === "all" ? styles.filterTabActive : ""
            }`}
          >
            All Events
          </button>
          <button
            onClick={() => handleFilterSelection("nearby")}
            className={`${styles.filterTabBtn} ${
              radarFilter === "nearby" ? styles.filterTabActive : ""
            }`}
          >
            📍 Nearby Me
          </button>
          <button
            onClick={() => handleFilterSelection("interests")}
            className={`${styles.filterTabBtn} ${
              radarFilter === "interests" ? styles.filterTabActive : ""
            }`}
          >
            My Interests
          </button>
        </div>

        {/* Horizontal scrollable event cards */}
        <div className={styles.horizontalScrollContainer}>
          {filteredRadarEvents.length === 0 ? (
            <p className={styles.emptyRadarText}>
              No events found for this filter.
            </p>
          ) : (
            filteredRadarEvents.map((e) => {
              const isSelected = selectedEvent?.id === e.id;
              const locationLower = (e.location || "").toLowerCase();
              const catObj = getCategoryById(e.category);

              return (
                <div
                  key={e.id}
                  onClick={() => handleSelectEvent(e)}
                  className={`${styles.scrollItem} ${styles.eventCard} ${
                    isSelected
                      ? styles.eventCardSelected
                      : styles.eventCardDefault
                  }`}
                >
                  <div className={styles.cardTopRow}>
                    <div>
                      <span className={styles.cardCategory}>
                        {catObj.icon} {catObj.label}
                      </span>
                      <h4 className={styles.cardTitle}>{e.title}</h4>
                    </div>
                    <div
                      className={`${styles.cardThumbnail} ${
                        e.color || "bg-brand"
                      }`}
                    >
                      {e.title.substring(0, 2).toUpperCase()}
                    </div>
                  </div>

                  <div className={styles.cardDetailsGroup}>
                    <p className={styles.cardDetailText}>
                      <Calendar size={12} className={styles.accentIcon} />{" "}
                      {e.date}
                    </p>
                    <p className={styles.cardAddressText}>
                      <MapPin size={12} className={styles.addressPinIcon} />{" "}
                      {e.location}{" "}
                      {e.distance !== null && e.distance !== undefined && (
                        <span className={styles.distanceText}>
                          (
                          {e.distance < 1
                            ? `${Math.round(e.distance * 1000)}m`
                            : `${e.distance.toFixed(1)} km`}
                          )
                        </span>
                      )}
                    </p>
                  </div>

                  <div className={styles.cardFooter}>
                    <span
                      className={`${styles.areaBadge} ${
                        locationLower.includes("howth")
                          ? styles.areaBadgeBlue
                          : locationLower.includes("phoenix park")
                          ? styles.areaBadgeAmber
                          : styles.areaBadgeEmerald
                      }`}
                    >
                      <Navigation size={10} />
                      {locationLower.includes("howth")
                        ? "Coastal Area (Howth)"
                        : locationLower.includes("phoenix park")
                        ? "Northwest Dublin"
                        : "Dublin City Center"}
                    </span>
                    {/* Opens the full detail screen via onViewDetails, not onSelectEvent */}
                    <button
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (onViewDetails) {
                          onViewDetails(e);
                        }
                      }}
                      className={styles.cardDetailsLink}
                    >
                      View Details <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
